<template>
    <div>
        <TransitionFade>
            <div @click="emitCloseEvent" class="fixed left-0 top-0 w-full h-full backdrop-blur-xl z-40" v-if="state"></div>
        </TransitionFade>

        <div class="filter" :class="{'filter-open': state}">

            <!-- Form -->
            <form class="flex flex-col justify-between h-full" @submit.prevent="handleFilter">
                <div class="overflow-y-auto">
                    <button
                        type="button"
                        @click="emitCloseEvent"
                        class="text-white flex gap-4 items-center p-8 border-b border-white border-opacity-10 w-full"
                    >
                        <AtomIcArrow :width="15" :height="14"/>
                        {{ $t('search.filter') }}
                    </button>

                    <MoleculeCollapse :title="$t('search.filters.period')" :default-MoleculeCollapse="false">
                        <div class="flex gap-8 pb-8 items-center flex-wrap">
                            <div class="flex justify-between items-center gap-3 w-full md:w-auto">
                                <span class="text-white w-[100px] md:w-auto">{{ $t('search.filters.from') }}</span>
                                <MoleculeSelectInput v-model="period.start" :options="fromYearOptions" />
                            </div>
                            <div class="flex justify-between items-center gap-3 w-full md:w-auto">
                                <span class="text-white w-[100px] md:w-auto">{{ $t('search.filters.to') }}</span>
                                <MoleculeSelectInput v-model="period.end" :options="toYearOptions" />
                            </div>
                        </div>
                    </MoleculeCollapse>

                    <MoleculeCollapse
                        v-for="category in orderedCategories"
                        :key="category.id"
                        :title="$t('search.filters.categories.' + category.name)"
                        :defaultCollapse="false"
                    >
                        <div class="pb-8 flex gap-3 items-center flex-wrap">
                            <AtomLabelSelect
                                v-for="(subcategory, subCategoryIndex) in category.subcategories"
                                :key="subCategoryIndex"
                                :title="subcategory.name"
                                :selected="selectedSubcategories.includes(subcategory.id)"
                                @click.native="toggleSelectedSubcategory(subcategory.id)"
                            />
                        </div>
                    </MoleculeCollapse>

                    <button @click="resetForm" type="button" class="text-white m-8 underline opacity-70">
                        {{ $t('search.filters.reset') }}
                    </button>
                </div>

                <button
                    type="submit"
                    class="bg-pink w-full block h-20 text-white shrink-0 hover:opacity-90 transition ease-in-out duration-200"
                >
                    {{ $t('search.filters.validate') }}
                </button>
            </form>
            <!-- Form -->

        </div>
    </div>
</template>

<script>
import AtomIcArrow from "../atoms/AtomIcArrow.vue";
import TransitionFade from "../../../transitions/TransitionFade.vue";
import _ from "lodash";
import AtomLabelSelect from "../atoms/AtomLabelSelect.vue";
import MoleculeCollapse from "../molecules/MoleculeCollapse.vue";
import MoleculeSelectInput from "../../global/molecules/MoleculeSelectInput.vue";
import {mapActions, mapState} from "vuex";

export default {
    name: "OrganismFilters",
    components: {MoleculeSelectInput, MoleculeCollapse, AtomLabelSelect, AtomIcArrow, TransitionFade},
    props: {
        state: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            period:{
                start: "",
                end: ""
            },
            selectedSubcategories: [],
            categoriesOrder: ['heading', 'artistic_field', 'location']
        };
    },
    computed: {
        ...mapState('search', ['categories']),

        orderedCategories() {
            return this.categories?.slice().sort((a, b) => {
                const orderA = this.categoriesOrder.indexOf(a.key);
                const orderB = this.categoriesOrder.indexOf(b.key);

                return orderA - orderB;
            });
        },

        yearOptions() {
            const currentYear = new Date().getFullYear();
            const startYear = 2010;
            const years = [];

            for (let year = startYear; year <= currentYear; year++) {
                years.push(year.toString());
            }

            return years.reverse();
        },
        fromYearOptions() {
            return this.yearOptions;
        },
        toYearOptions() {
            return this.yearOptions.filter(year => +year >= +this.period.start);
        }
    },
    methods: {
        ...mapActions('search', ['getCategories']),

        emitCloseEvent() {
            this.$emit('close');
        },

        toggleSelectedSubcategory(subcategoryId) {
            const subCatIndex = this.selectedSubcategories.findIndex(id => id === subcategoryId);

            if (subCatIndex !== -1) {
                this.selectedSubcategories.splice(subCatIndex, 1);
            } else {
                this.selectedSubcategories.push(subcategoryId);
            }
        },

        handleFilter(){
            this.emitCloseEvent()
            this.updateUrl()
            this.$emit('search');
        },

        updateUrl() {
            const queryParams = this.getExistingQueryParams();
            this.addSelectedOptionsToQueryParams(queryParams);

            const queryString = queryParams.join("&");
            this.updateUrlWithQueryString(queryString);
        },

        getExistingQueryParams() {
            const existingQuery = this.$route.query;
            const queryParams = [];

            if (existingQuery.q){
                queryParams.push(`q=${existingQuery.q}`);
            }
            if (existingQuery.tab) {
                queryParams.push(`tab=${existingQuery.tab}`);
            }

            return queryParams;
        },

        addSelectedOptionsToQueryParams(queryParams) {
            if (this.period.start !== "") {
                queryParams.push(`periodStart=${this.period.start}`);
            }

            if (this.period.end !== "") {
                queryParams.push(`periodEnd=${this.period.end}`);
            }

            if (this.selectedSubcategories.length > 0) {
                const subcategoryParam = `selectedSubcategories=${this.selectedSubcategories.join(',')}`;
                queryParams.push(subcategoryParam);
            }
        },

        updateUrlWithQueryString(queryString) {
            const query = {};
            queryString
                .split('&')
                .forEach(item => {
                    const [key, value] = item.split('=');
                    query[key] = value;
                });

            if (!_.isEqual(query, this.$route.query)){
                this.$router.push({ query });
            }
        },

        resetForm() {
            this.period.start = "";
            this.period.end = "";
            this.selectedSubcategories = []
        },
    },

    mounted() {
        this.getCategories()

        // Sync url parameters with filters
        const { periodStart, periodEnd, selectedSubcategories } = this.$route.query;

        this.period.start = periodStart || "";
        this.period.end = periodEnd || "";
        this.selectedSubcategories = selectedSubcategories ? selectedSubcategories.split(',').map(Number) : [];
    }
}
</script>

<style scoped>
.filter{
    @apply h-[100dvh] bg-darkgray md:max-w-[560px] fixed top-0 right-0 z-50 w-full overflow-hidden;
    transform: translateX(100%);
    transition-duration: 0.5s;
}
.filter-open{
    transform: translateX(0%);
}
</style>
